'use client'

import { Common, dynamic, Messages } from '@/config/NextImport'
import Link from 'next/link'
import { useEffect, useMemo, useRef, useState } from 'react'
import { usePathname } from 'next/navigation'
import { BUSINESS_NAP } from '@/constants/businessNap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBluesky,
  faFacebookF,
  faInstagram,
  faPinterestP,
  faSnapchat,
  faTiktok,
  faXTwitter,
  faYoutube
} from '@fortawesome/free-brands-svg-icons'
import { useSelector } from 'react-redux'
import { AppState } from '@/lib/store'

const CookieBox = dynamic(() => import('./CookieBox'))

const Footer = () => {
  const pathname = usePathname()
  const [isForceUpdate, setForceUpdate] = useState<boolean>(false)
  const [customerCareNumber, setCustomerCareNumber] = useState<any>('')
  const [customerCareEmail, setCustomerCareEmail] = useState<any>('')
  const [storeAddress, setStoreAddress] = useState<any>('')
  const chatNumber = useRef<any>(process.env.NEXT_PUBLIC_CHAT_NUMBER || '')
  const { settingOptions } = useSelector((state: AppState) => state.settings)

  const [socialLinks, setSocialLinks] = useState<any>({
    facebookUrl: '',
    instagramUrl: '',
    xtwitterUrl: '',
    pinterestUrl: '',
    tiktokUrl: '',
    snapchatUrl: '',
    youtubeUrl: '',
    blueskyUrl: ''
  })

  const [show, setShow] = useState(false)

  useEffect(() => {
    if (settingOptions?.length > 0) {
      let customerCarePhone = ''
      let customerCareEmail = ''
      let storeAddress = ''
      let facebookUrl = ''
      let instagramUrl = ''
      let xtwitterUrl = ''
      let pinterestUrl = ''
      let tiktokUrl = ''
      let snapchatUrl = ''
      let youtubeUrl = ''
      let blueskyUrl = ''
      settingOptions.map((item: any) => {
        if (item[Messages.HarmonySettingKey] === Messages.CustomerCareNumber)
          customerCarePhone = item[Messages.GlobalSettingValue]
        if (item[Messages.HarmonySettingKey] === Messages.CustomerSupportEmail)
          customerCareEmail = item[Messages.GlobalSettingValue]
        if (item[Messages.HarmonySettingKey] === Messages.StoreAddress)
          storeAddress = item[Messages.GlobalSettingValue]
        if (item[Messages.HarmonySettingKey] === Messages.FacebookUrl)
          facebookUrl = item[Messages.GlobalSettingValue]
        if (item[Messages.HarmonySettingKey] === Messages.InstagramUrl)
          instagramUrl = item[Messages.GlobalSettingValue]
        if (item[Messages.HarmonySettingKey] === Messages.XtwitterUrl)
          xtwitterUrl = item[Messages.GlobalSettingValue]
        if (item[Messages.HarmonySettingKey] === Messages.PinterestUrl)
          pinterestUrl = item[Messages.GlobalSettingValue]
        if (item[Messages.HarmonySettingKey] === Messages.TiktokUrl)
          tiktokUrl = item[Messages.GlobalSettingValue]
        if (item[Messages.HarmonySettingKey] === Messages.SnapchatUrl)
          snapchatUrl = item[Messages.GlobalSettingValue]
        if (item[Messages.HarmonySettingKey] === Messages.YoutubeUrl)
          youtubeUrl = item[Messages.GlobalSettingValue]
        if (item[Messages.HarmonySettingKey] === Messages.BlueskyUrl)
          blueskyUrl = item[Messages.GlobalSettingValue]
        return true
      })
      setSocialLinks({
        facebookUrl,
        instagramUrl,
        xtwitterUrl,
        pinterestUrl,
        tiktokUrl,
        snapchatUrl,
        youtubeUrl,
        blueskyUrl
      })
      setCustomerCareNumber(customerCarePhone)
      setCustomerCareEmail(customerCareEmail)
      setStoreAddress(storeAddress)
      setForceUpdate(!isForceUpdate)
    }
  }, [settingOptions])

  const popupConfig = useMemo(() => {
    if (pathname?.includes('engagement-rings')) {
      return {
        enabled: true,
        title: 'Looking for the perfect engagement ring?',
        subtitle: 'Our ring specialists are here to guide you.'
      }
    }

    if (pathname?.includes('loose-diamonds')) {
      return {
        enabled: true,
        title: 'Searching for the perfect diamond?',
        subtitle: 'Our diamond experts are delighted to assist you.'
      }
    }
    return { enabled: false }
  }, [pathname])

  useEffect(() => {
    if (!popupConfig.enabled) {
      setShow(false)
      return
    }

    const timer = setTimeout(() => {
      setShow(true)
    }, 60000)

    return () => clearTimeout(timer)
  }, [popupConfig.enabled])

  return (
    <footer className="footer">
      <CookieBox />
      <div className="footer-top">
        <div className="container-fluid">
          <div className="row row-cols-md-2 row-cols-sm-2 row-cols-1">
            <div className="col col-md-4 col-lg-3 first-col">
              <div className="footer-widget footer-link">
                <div className="footer-link-title">Harmony Jewels</div>
                <ul className="footer-link-list">
                  <li>
                    <Link href="/about-us">About</Link>
                  </li>
                  <li>
                    <Link href="/bespoke-engagement-rings">Bespoke Engagement Rings</Link>
                  </li>
                  <li>
                    <Link
                      href="https://g.page/r/CQtX4yx4vpL7EAo"
                      rel="noopener"
                    >
                      Reviews
                    </Link>
                  </li>
                  <li>
                    <Link href="/blogs">Blogs</Link>
                  </li>
                  <li>
                    <Link href="/contact-us">Contact Us</Link>
                  </li>
                  <li>
                    <Link href="/sitemap">Site Map</Link>
                  </li>
                </ul>
              </div>
            </div>
            <div className="col col-md-4 col-lg-3">
              <div className="footer-widget footer-link">
                <div className="footer-link-title">Customer Care</div>
                <ul className="footer-link-list">
                  <li>
                    <Link href="/lifetime-cleaning">Lifetime Cleaning</Link>
                  </li>
                  <li>
                    <Link href="/manufacturers-warranty">Manufacturers Warranty</Link>
                  </li>
                </ul>
              </div>
            </div>

            <div className="col col-md-4 col-lg-3">
              <div className="footer-widget footer-link">
                <div className="footer-link-title">Policies</div>
                <ul className="footer-link-list">
                  <li>
                    <Link href="/tax-duty">Tax and Duty</Link>
                  </li>
                  <li>
                    <Link href="/return-policy">Returns & Refunds Policy</Link>
                  </li>
                  <li>
                    <Link href="/privacy-policy">Privacy Policy</Link>
                  </li>
                  <li>
                    <Link href="/cookie-policy">Cookies Policy</Link>
                  </li>
                  <li>
                    <Link href="/terms-conditions">Terms & Conditions</Link>
                  </li>
                </ul>
              </div>
            </div>

            <div className="col col-md-4 col-lg-3 last-col">
              <div className="footer-widget footer-link">
                <div className="footer-link-title">Contact Us</div>
                <ul className="footer-detail footer-link-list">
                  <li>
                    {customerCareNumber && (
                      <Link href={'tel:' + customerCareNumber}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#535353"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feather feather-phone"
                        >
                          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                        </svg>
                        {customerCareNumber}
                      </Link>
                    )}
                    
                  </li>
                  <li>
                    {customerCareNumber && (
                      <a href={'iMessage://' + customerCareNumber}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#535353"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feather feather-message-circle"
                        >
                          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                        </svg>
                        iMessage
                      </a>
                    )}
                    
                  </li>
                  <li>
                    {customerCareEmail && (
                      <Link
                        href={'mailto:' + customerCareEmail}
                        rel="noopener"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="#535353"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="feather feather-mail"
                        >
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                          <polyline points="22,6 12,13 2,6"></polyline>
                        </svg>
                        {customerCareEmail}
                      </Link>
                    )}
                    
                  </li>
                  <li>
                    <span className="footer-contact-details">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="feather feather-clock"
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                        ></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                      {BUSINESS_NAP.hoursWeekdayLine}
                      <br /> {BUSINESS_NAP.hoursSundayLine}
                    </span>
                  </li>
                  <li>
                    <span className="footer-contact-details">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#535353"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M21 10H3M21 12.5V8.8C21 7.11984 21 6.27976 20.673 5.63803C20.3854 5.07354 19.9265 4.6146 19.362 4.32698C18.7202 4 17.8802 4 16.2 4H7.8C6.11984 4 5.27976 4 4.63803 4.32698C4.07354 4.6146 3.6146 5.07354 3.32698 5.63803C3 6.27976 3 7.11984 3 8.8V17.2C3 18.8802 3 19.7202 3.32698 20.362C3.6146 20.9265 4.07354 21.3854 4.63803 21.673C5.27976 22 6.11984 22 7.8 22H12M16 2V6M8 2V6M14.5 19L16.5 21L21 16.5" />
                      </svg>
                      Walk-ins welcome, appointments recommended
                    </span>
                  </li>
                  <li>
                    <Link
                      href="https://goo.gl/maps/DMPiYthseUfYeqjA7"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#535353"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="feather feather-map-pin"
                      >
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle
                          cx="12"
                          cy="10"
                          r="3"
                        ></circle>
                      </svg>
                      {storeAddress || BUSINESS_NAP.addressDisplay}
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="footer-bottom">
        <div className="container-fluid">
          <div className="row align-items-center justify-content-between">
            <div className="col-md-6 col-lg-4 order-1 order-md-1">
              <div className="footer-social">
                <ul className="social-bar">
                  <li>
                    <Link
                      href={socialLinks.facebookUrl}
                      target="_black"
                    >
                      <FontAwesomeIcon icon={faFacebookF} />
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={socialLinks.instagramUrl}
                      target="_black"
                    >
                      <FontAwesomeIcon icon={faInstagram} />
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={socialLinks.pinterestUrl}
                      target="_black"
                    >
                      <FontAwesomeIcon icon={faPinterestP} />
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={socialLinks.tiktokUrl}
                      target="_black"
                    >
                      <FontAwesomeIcon icon={faTiktok} />
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={socialLinks.xtwitterUrl}
                      target="_black"
                    >
                      <FontAwesomeIcon icon={faXTwitter} />
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={socialLinks.snapchatUrl}
                      target="_black"
                    >
                      <FontAwesomeIcon icon={faSnapchat} />
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={socialLinks.youtubeUrl}
                      target="_black"
                    >
                      <FontAwesomeIcon icon={faYoutube} />
                    </Link>
                  </li>
                  <li>
                    <Link
                      href={socialLinks.blueskyUrl}
                      target="_black"
                    >
                      <FontAwesomeIcon icon={faBluesky} />
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
            <div className="col-md-12 col-lg-4 py-0 py-md-3 py-lg-0 order-3 order-lg-2">
              <div className="footer-copyright text-center">
                <p>© {new Date().getFullYear()} Harmony Jewels. All Rights Reserved.</p>
              </div>
            </div>
            <div className="col-md-6 col-lg-4 order-3 order-md-2 order-lg-3">
              <div className="footer-payment">
                <img
                  src={Common.setImagePath('assets/images/icon/Payment+icon.svg')}
                  title="Payment Type"
                  alt="Payment Type"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {chatNumber.current !== undefined ? (
        <div className="whatsapp-chat-box">
          <a
            href={'https://wa.me/' + chatNumber.current}
            target="_blank"
            rel="noopener noreferrer"
            className="whatsapp-chat"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              x="0px"
              y="0px"
              width="100"
              height="100"
              viewBox="0 0 48 48"
            >
              <path
                fill="#fff"
                d="M4.868,43.303l2.694-9.835C5.9,30.59,5.026,27.324,5.027,23.979C5.032,13.514,13.548,5,24.014,5c5.079,0.002,9.845,1.979,13.43,5.566c3.584,3.588,5.558,8.356,5.556,13.428c-0.004,10.465-8.522,18.98-18.986,18.98c-0.001,0,0,0,0,0h-0.008c-3.177-0.001-6.3-0.798-9.073-2.311L4.868,43.303z"
              ></path>
              <path
                fill="#fff"
                d="M4.868,43.803c-0.132,0-0.26-0.052-0.355-0.148c-0.125-0.127-0.174-0.312-0.127-0.483l2.639-9.636c-1.636-2.906-2.499-6.206-2.497-9.556C4.532,13.238,13.273,4.5,24.014,4.5c5.21,0.002,10.105,2.031,13.784,5.713c3.679,3.683,5.704,8.577,5.702,13.781c-0.004,10.741-8.746,19.48-19.486,19.48c-3.189-0.001-6.344-0.788-9.144-2.277l-9.875,2.589C4.953,43.798,4.911,43.803,4.868,43.803z"
              ></path>
              <path
                fill="none"
                d="M24.014,5c5.079,0.002,9.845,1.979,13.43,5.566c3.584,3.588,5.558,8.356,5.556,13.428c-0.004,10.465-8.522,18.98-18.986,18.98h-0.008c-3.177-0.001-6.3-0.798-9.073-2.311L4.868,43.303l2.694-9.835C5.9,30.59,5.026,27.324,5.027,23.979C5.032,13.514,13.548,5,24.014,5 M24.014,42.974C24.014,42.974,24.014,42.974,24.014,42.974C24.014,42.974,24.014,42.974,24.014,42.974 M24.014,42.974C24.014,42.974,24.014,42.974,24.014,42.974C24.014,42.974,24.014,42.974,24.014,42.974 M24.014,4C24.014,4,24.014,4,24.014,4C12.998,4,4.032,12.962,4.027,23.979c-0.001,3.367,0.849,6.685,2.461,9.622l-2.585,9.439c-0.094,0.345,0.002,0.713,0.254,0.967c0.19,0.192,0.447,0.297,0.711,0.297c0.085,0,0.17-0.011,0.254-0.033l9.687-2.54c2.828,1.468,5.998,2.243,9.197,2.244c11.024,0,19.99-8.963,19.995-19.98c0.002-5.339-2.075-10.359-5.848-14.135C34.378,6.083,29.357,4.002,24.014,4L24.014,4z"
              ></path>
              <path
                fill="#40c351"
                d="M35.176,12.832c-2.98-2.982-6.941-4.625-11.157-4.626c-8.704,0-15.783,7.076-15.787,15.774c-0.001,2.981,0.833,5.883,2.413,8.396l0.376,0.597l-1.595,5.821l5.973-1.566l0.577,0.342c2.422,1.438,5.2,2.198,8.032,2.199h0.006c8.698,0,15.777-7.077,15.78-15.776C39.795,19.778,38.156,15.814,35.176,12.832z"
              ></path>
              <path
                fill="#fff"
                fillRule="evenodd"
                d="M19.268,16.045c-0.355-0.79-0.729-0.806-1.068-0.82c-0.277-0.012-0.593-0.011-0.909-0.011c-0.316,0-0.83,0.119-1.265,0.594c-0.435,0.475-1.661,1.622-1.661,3.956c0,2.334,1.7,4.59,1.937,4.906c0.237,0.316,3.282,5.259,8.104,7.161c4.007,1.58,4.823,1.266,5.693,1.187c0.87-0.079,2.807-1.147,3.202-2.255c0.395-1.108,0.395-2.057,0.277-2.255c-0.119-0.198-0.435-0.316-0.909-0.554s-2.807-1.385-3.242-1.543c-0.435-0.158-0.751-0.237-1.068,0.238c-0.316,0.474-1.225,1.543-1.502,1.859c-0.277,0.317-0.554,0.357-1.028,0.119c-0.474-0.238-2.002-0.738-3.815-2.354c-1.41-1.257-2.362-2.81-2.639-3.285c-0.277-0.474-0.03-0.731,0.208-0.968c0.213-0.213,0.474-0.554,0.712-0.831c0.237-0.277,0.316-0.475,0.474-0.791c0.158-0.317,0.079-0.594-0.04-0.831C20.612,19.329,19.69,16.983,19.268,16.045z"
                clipRule="evenodd"
              ></path>
            </svg>
          </a>
          <div
            className={`popup whatsapp-chat-box-content ${
              show && popupConfig.enabled ? 'show' : ''
            }`}
          >
            <div
              className="whatsapp-chat-close"
              onClick={(e) => {
                e.stopPropagation()
                setShow(false)
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>

            <Link
              href={'https://wa.me/' + chatNumber.current}
              className="chat-box-content"
              target="_blank"
              onClick={(e) => e.stopPropagation()}
            >
              <p className="mb-0">{popupConfig?.title}</p>
              <small className="fw-semibold">{popupConfig?.subtitle}</small>
            </Link>
          </div>
        </div>
      ) : (
        ''
      )}
      <div className="top-scroll">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="feather feather-chevron-right"
        >
          <polyline points="9 18 15 12 9 6"></polyline>
        </svg>
      </div>
    </footer>
  )
}

export default Footer
